import { optionOrder, effectiveScale, MAX_OTHER_TEXT, type AnswerValue, type Question } from '@survey/core';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useMemo } from 'react';
import { mediaUrl, type FileAnswer } from '../api/client';
import { useI18n } from '../i18n';
import { ConstantSumInput, FileInput, LocationInput, MatrixInput, NpsInput, SignatureInput, SliderInput, TextFormInput } from './AdvancedInputs';

interface Props {
  question: Question;
  value: AnswerValue | undefined;
  onChange: (v: AnswerValue) => void;
  /** Seed for option randomization; stable per respondent. */
  seed: string;
  invalid?: boolean;
  autoFocus?: boolean;
  /** "Other, please specify" text, for choice questions with an Other option. */
  other?: AnswerValue;
  onOtherChange?: (v: string) => void;
  /** Uploads a file for file-upload questions. */
  uploader?: (file: File) => Promise<FileAnswer>;
}

/** Renders the input control for one question type. */
export function QuestionInput(props: Props) {
  const { question: q, value, other, onOtherChange } = props;
  const { t } = useI18n();
  const otherOption = q.options?.find((o) => o.other);
  const chosen = Array.isArray(value) ? value : [value];
  const showOther = !!otherOption && chosen.includes(otherOption.id);
  return (
    <>
      <BaseInput {...props} />
      {showOther && (
        <input
          className="input mt-8 other-input"
          value={typeof other === 'string' ? other : ''}
          maxLength={MAX_OTHER_TEXT}
          placeholder={t('input.other_placeholder')}
          aria-label={`${otherOption!.label}: ${t('input.other_placeholder')}`}
          autoFocus={!other}
          onChange={(e) => onOtherChange?.(e.target.value)}
        />
      )}
    </>
  );
}

function BaseInput({ question: q, value, onChange, seed, invalid, autoFocus, uploader }: Props) {
  const { t } = useI18n();
  const options = useMemo(() => optionOrder(q, seed), [q, seed]);
  const id = `q-${q.id}`;
  const cls = invalid ? 'invalid' : '';
  const hasImages = options.some((o) => o.imageUrl);
  const label = (o: { label: string; imageUrl?: string }) =>
    o.imageUrl ? (
      <span className="choice-body">
        <img src={mediaUrl(o.imageUrl)} alt="" loading="lazy" />
        <span>{o.label}</span>
      </span>
    ) : (
      <span>{o.label}</span>
    );

  switch (q.type) {
    case 'short_text':
      return (
        <input
          id={id}
          className={`input ${cls}`}
          style={{ height: 46, fontSize: 15 }}
          value={typeof value === 'string' ? value : ''}
          placeholder={q.placeholder || t('input.type_answer')}
          maxLength={q.validation?.maxLength ?? 10000}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'long_text':
      return (
        <textarea
          id={id}
          className={`textarea ${cls}`}
          style={{ minHeight: 140, fontSize: 15 }}
          value={typeof value === 'string' ? value : ''}
          placeholder={q.placeholder || t('input.type_answer')}
          maxLength={q.validation?.maxLength ?? 10000}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'number':
      return (
        <input
          id={id}
          className={`input ${cls}`}
          style={{ height: 46, fontSize: 15, maxWidth: 260 }}
          type="number"
          inputMode="decimal"
          step={q.validation?.integer ? 1 : 'any'}
          min={q.validation?.min}
          max={q.validation?.max}
          value={typeof value === 'number' ? value : ''}
          placeholder={q.placeholder || '0'}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
    case 'date':
      return (
        <input
          id={id}
          className={`input ${cls}`}
          style={{ height: 46, fontSize: 15, maxWidth: 260 }}
          type="date"
          value={typeof value === 'string' ? value : ''}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
    case 'dropdown':
      return (
        <select id={id} className={`select ${cls}`} style={{ height: 46, fontSize: 15 }} value={typeof value === 'string' ? value : ''} autoFocus={autoFocus} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">{t('input.select_option')}</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      );
    case 'single_choice':
      return (
        <div className={`choice-list ${hasImages ? 'with-images' : ''}`} role="radiogroup" aria-labelledby={`${id}-title`}>
          {options.map((o) => (
            <label key={o.id} className={`choice ${value === o.id ? 'selected' : ''}`}>
              <input type="radio" name={id} checked={value === o.id} onChange={() => onChange(o.id)} />
              <span className="mark radio" aria-hidden />
              {label(o)}
            </label>
          ))}
        </div>
      );
    case 'multiple_choice': {
      const selected = Array.isArray(value) ? value : [];
      return (
        <div className={`choice-list ${hasImages ? 'with-images' : ''}`} role="group" aria-labelledby={`${id}-title`}>
          {options.map((o) => {
            const on = selected.includes(o.id);
            return (
              <label key={o.id} className={`choice ${on ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => onChange(on ? selected.filter((x) => x !== o.id) : [...selected, o.id])}
                />
                <span className="mark" aria-hidden />
                {label(o)}
              </label>
            );
          })}
        </div>
      );
    }
    case 'ranking': {
      const ranked = Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : options.map((o) => o.id);
      const move = (index: number, delta: number) => {
        const next = [...ranked];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target]!, next[index]!];
        onChange(next);
      };
      return <div className="stack gap-8">{ranked.map((id, index) => {
        const option = q.options?.find((o) => o.id === id);
        if (!option) return null;
        return <div className="row gap-8" key={id}><span className="pill">{index + 1}</span><span className="grow row gap-8">{option.imageUrl && <img className="rank-img" src={mediaUrl(option.imageUrl)} alt="" loading="lazy" />}{option.label}</span><button type="button" className="btn ghost sm icon" aria-label={`${t('qe.move_up')} ${option.label}`} disabled={index === 0} onClick={() => move(index, -1)}><ArrowUp /></button><button type="button" className="btn ghost sm icon" aria-label={`${t('qe.move_down')} ${option.label}`} disabled={index === ranked.length - 1} onClick={() => move(index, 1)}><ArrowDown /></button></div>;
      })}</div>;
    }
    case 'yes_no':
      return (
        <div className="choice-row" role="radiogroup" aria-labelledby={`${id}-title`}>
          {[
            { v: true, label: t('common.yes') },
            { v: false, label: t('common.no') },
          ].map((o) => (
            <label key={o.label} className={`choice ${value === o.v ? 'selected' : ''}`}>
              <input type="radio" name={id} checked={value === o.v} onChange={() => onChange(o.v)} />
              <span className="mark radio" aria-hidden />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      );
    case 'matrix':
      return <MatrixInput question={q} value={value} onChange={onChange} seed={seed} invalid={invalid} />;
    case 'slider':
      return <SliderInput question={q} value={value} onChange={onChange} seed={seed} invalid={invalid} />;
    case 'nps':
      return <NpsInput question={q} value={value} onChange={onChange} seed={seed} />;
    case 'constant_sum':
      return <ConstantSumInput question={q} value={value} onChange={onChange} seed={seed} invalid={invalid} />;
    case 'text_form':
      return <TextFormInput question={q} value={value} onChange={onChange} seed={seed} invalid={invalid} />;
    case 'file_upload':
      return <FileInput question={q} value={value} onChange={onChange} seed={seed} uploader={uploader} />;
    case 'signature':
      return <SignatureInput question={q} value={value} onChange={onChange} seed={seed} invalid={invalid} />;
    case 'location':
      return <LocationInput question={q} value={value} onChange={onChange} seed={seed} />;
    case 'descriptive':
      return null;
    case 'rating': {
      const s = effectiveScale(q);
      const points = Array.from({ length: s.max - s.min + 1 }, (_, i) => s.min + i);
      return (
        <div>
          <div className="rating" role="radiogroup" aria-labelledby={`${id}-title`}>
            {points.map((n) => (
              <label key={n} className={`rating-pt ${value === n ? 'selected' : ''}`}>
                <input type="radio" name={id} checked={value === n} onChange={() => onChange(n)} />
                {n}
              </label>
            ))}
          </div>
          {(s.minLabel || s.maxLabel) && (
            <div className="row small muted mt-8">
              <span>{s.minLabel}</span>
              <span className="grow" />
              <span>{s.maxLabel}</span>
            </div>
          )}
        </div>
      );
    }
  }
}
